/**
 * Hydrate in-memory compile store + WASM host from a persisted IndexedDB build.
 */

import { getBuild, getProject } from "./db";
import { DEFAULT_CSPROJ_PATH } from "./project-fs";
import {
  compileIndicatorProject,
  ensureIndicatorRuntime,
  loadCompiledProject,
} from "./runtime/IndicatorRuntimeManager";
import { getLoadedRuntimeBuiltAt } from "./runtime/indicator-runtime-host";
import { useIndicatorCompileStore } from "./runtime/compile-store";
import { useIndicatorLoggerStore } from "./runtime/logger-store";
import type { CompileResult } from "./runtime-types";

const hydrateInflight = new Map<string, Promise<CompileResult | undefined>>();

export function invalidatePersistedProjectCompile(projectId: string): void {
  useIndicatorCompileStore.getState().clearCompileResult(projectId);
  hydrateInflight.delete(projectId);
}

export type EnsureCompiledOptions = {
  /** Reload assembly from IndexedDB even when a prior in-memory compile exists. */
  force?: boolean;
};

/**
 * Load persisted build into WASM and the session compile store.
 * Call after binary import/install so chart add works without a full page refresh.
 */
export async function warmProjectFromPersistedBuild(
  projectId: string
): Promise<CompileResult | undefined> {
  await ensureIndicatorRuntime();
  invalidatePersistedProjectCompile(projectId);
  return ensureCompiledFromBuild(projectId, { force: true });
}

/**
 * Studio/catalog preview: load persisted assembly into WASM even when compile-store
 * already has metadata-only success (avoids skipping real LoadCompiledProject).
 */
export async function loadPersistedBuildIntoRuntime(
  projectId: string
): Promise<CompileResult | undefined> {
  await ensureIndicatorRuntime();
  return ensureCompiledFromBuild(projectId, { force: true });
}

export async function ensureCompiledFromBuild(
  projectId: string,
  options: EnsureCompiledOptions = {}
): Promise<CompileResult | undefined> {
  await ensureIndicatorRuntime();

  if (!options.force) {
    const existing = useIndicatorCompileStore.getState().getCompileResult(projectId);
    if (existing?.success) return existing;
  } else {
    invalidatePersistedProjectCompile(projectId);
  }

  const inflight = hydrateInflight.get(projectId);
  if (inflight) return inflight;

  const promise = (async () => {
    const build = await getBuild(projectId);
    if (!build?.assemblyBase64) return undefined;

    const project = await getProject(projectId);
    const csprojPath = project?.csprojFilePath ?? DEFAULT_CSPROJ_PATH;
    let csprojXml = "";
    let projectFiles: { path: string; type: string; content?: string }[] = [];
    if (project) {
      const { listFiles } = await import("./db");
      projectFiles = await listFiles(projectId);
      const csproj = projectFiles.find(
        (f) => f.path === csprojPath && f.type === "file"
      );
      csprojXml = csproj?.content ?? "";
    }

    if (!csprojXml.trim()) {
      const { defaultCsprojXml } = await import("./csproj");
      csprojXml = defaultCsprojXml({
        name: project?.name ?? build.meta?.name ?? "Indicator",
        version: project?.version ?? build.meta?.version ?? "1.0",
        description: project?.description ?? "",
        overlay: project?.overlay ?? build.meta?.overlay ?? true,
        entryFile: project?.entryFilePath ?? "Indicator.cs",
        iconFile: project?.iconFilePath,
        kind: project?.kind ?? "indicator",
      });
    }

    // IN-07: reject SDK-mismatched persisted DLLs. Builds record the runtime
    // builtAt they were compiled against; if the currently-loaded runtime differs,
    // recompile from source (when available) instead of silently loading the DLL.
    const currentRuntimeBuiltAt = getLoadedRuntimeBuiltAt();
    if (
      build.runtimeBuiltAt &&
      currentRuntimeBuiltAt &&
      build.runtimeBuiltAt !== currentRuntimeBuiltAt
    ) {
      const logger = useIndicatorLoggerStore.getState();
      const sourceFiles: Record<string, string> = {};
      for (const f of projectFiles) {
        if (f.type === "file" && typeof f.content === "string") {
          sourceFiles[f.path] = f.content;
        }
      }
      const hasSource =
        (project?.hasSource ?? false) &&
        Object.keys(sourceFiles).some((p) => p.endsWith(".cs"));

      if (hasSource && csprojXml.trim()) {
        logger.appendBuild(
          `[Build] Persisted assembly for ${projectId} was built against an older runtime (${build.runtimeBuiltAt} vs ${currentRuntimeBuiltAt}) — recompiling from source…`
        );
        const recompiled = await compileIndicatorProject({
          projectId,
          csprojXml,
          files: sourceFiles,
        });
        if (recompiled.success) {
          const { persistBuildFromCompile } = await import("./build-persist");
          await persistBuildFromCompile(projectId, recompiled);
          return recompiled;
        }
        logger.appendBuild(
          `[Build] Recompile after runtime update FAILED for ${projectId} — indicator unavailable until it compiles.`
        );
        return undefined;
      }

      logger.appendBuild(
        `[Build] ${projectId}: rebuild required — the persisted binary was built against runtime ${build.runtimeBuiltAt}, but the current runtime is ${currentRuntimeBuiltAt} and no source is available. Re-import or rebuild the indicator.`
      );
      return undefined;
    }

    const result = await loadCompiledProject({
      projectId,
      assemblyBase64: build.assemblyBase64,
      csprojXml,
    });
    return result.success ? result : undefined;
  })();

  hydrateInflight.set(projectId, promise);
  try {
    return await promise;
  } finally {
    hydrateInflight.delete(projectId);
  }
}
